// 视频处理相关工具函数

import { isValidVideoFile } from './helpers';

export interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
  format: string;
  size: number;
}

export interface VideoProcessingOptions {
  quality?: 'low' | 'medium' | 'high';
  format?: 'mp4' | 'webm' | 'mov';
  resolution?: {
    width?: number;
    height?: number;
  };
  bitrate?: number;
}

// 获取视频元数据
export async function getVideoMetadata(
  file: File
): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);

    video.addEventListener('loadedmetadata', () => {
      const metadata: VideoMetadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        format: file.type,
        size: file.size,
      };
      URL.revokeObjectURL(url);
      resolve(metadata);
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load video metadata'));
    });

    video.src = url;
  });
}

// 验证视频文件
export function validateVideoFile(
  file: File,
  maxSizeMB: number = 500
): { valid: boolean; error?: string } {
  // 检查文件类型
  if (!isValidVideoFile(file)) {
    return {
      valid: false,
      error: 'Invalid video file format. Supported formats: MP4, WebM, OGG, MOV, AVI',
    };
  }

  // 检查文件大小
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `Video file size exceeds ${maxSizeMB}MB limit`,
    };
  }

  return { valid: true };
}

// 获取视频缩略图
export async function generateVideoThumbnail(
  file: File,
  time: number = 0,
  width: number = 320
): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const url = URL.createObjectURL(file);

    video.addEventListener('loadeddata', () => {
      video.currentTime = time;
    });

    video.addEventListener('seeked', () => {
      canvas.width = width;
      canvas.height = (video.videoHeight / video.videoWidth) * width;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
      URL.revokeObjectURL(url);
      resolve(thumbnailUrl);
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to generate video thumbnail'));
    });

    video.src = url;
  });
}

// 提取音频 URL（用于预览）
export async function extractAudioUrl(
  file: File
): Promise<string> {
  // 在实际应用中，这需要后端支持
  // 这里返回文件 URL 作为示例
  return URL.createObjectURL(file);
}

// 估算视频处理时间
export function estimateProcessingTime(
  durationSeconds: number,
  steps: string[]
): number {
  // 基于经验值的估算
  const stepTimes: Record<string, number> = {
    asr: durationSeconds * 0.5, // ASR 处理时间约为视频时长的一半
    translation: durationSeconds * 0.1, // 翻译时间
    tts: durationSeconds * 0.3, // TTS 处理时间
    synthesis: durationSeconds * 0.6, // 视频合成时间
    upload: durationSeconds * 0.4, // 上传时间
  };

  let totalTime = 0;
  for (const step of steps) {
    totalTime += stepTimes[step] || 0;
  }

  return Math.ceil(totalTime);
}

// 生成视频处理选项
export function generateProcessingOptions(
  metadata: VideoMetadata,
  targetQuality: 'low' | 'medium' | 'high' = 'medium'
): VideoProcessingOptions {
  const options: VideoProcessingOptions = {
    quality: targetQuality,
    format: 'mp4',
  };

  switch (targetQuality) {
    case 'low':
      options.bitrate = 1000000; // 1 Mbps
      options.resolution = {
        width: Math.min(480, metadata.width),
        height: Math.min(360, metadata.height),
      };
      break;
    case 'medium':
      options.bitrate = 2500000; // 2.5 Mbps
      options.resolution = {
        width: Math.min(720, metadata.width),
        height: Math.min(480, metadata.height),
      };
      break;
    case 'high':
      options.bitrate = 5000000; // 5 Mbps
      options.resolution = {
        width: Math.min(1080, metadata.width),
        height: Math.min(720, metadata.height),
      };
      break;
  }

  return options;
}

// 检测视频是否包含音频
export async function hasAudioTrack(file: File): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);

    video.addEventListener('loadedmetadata', () => {
      const hasAudio = ((video as HTMLVideoElement & { webkitAudioDecodedByteCount?: number }).webkitAudioDecodedByteCount || 0) > 0;
      URL.revokeObjectURL(url);
      resolve(hasAudio);
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to check audio track'));
    });

    video.src = url;
  });
}